import { useCallback, useState } from 'react';
import { StyledTableDrawer } from '@/_shared/components/Table/StyledTable';
import { useDrawer } from '../components/Drawer';
import { Claims } from '../constants';
import { TypeOfDrawer } from '../types';

type HelperOpenDrawerProps = {
  drawers: StyledTableDrawer['instances'];
  key: TypeOfDrawer;
};

type HelperOpenDrawer = {
  callback: () => void;
  claimKey?: Claims;
  customLabel: string;
};

type UseDrawerList = {
  drawer: ReturnType<typeof useDrawer>;
  typeOfDrawer?: TypeOfDrawer;
  setTypeOfDrawer: (type: TypeOfDrawer | undefined) => void;
  openDrawer: (type: TypeOfDrawer) => void;
  helperOpenDrawer: ({ drawers, key }: HelperOpenDrawerProps) => HelperOpenDrawer | undefined;
};
/**
 * Custom hook `useDrawerList` to manage the state and behavior of a drawer component.
 *
 * This hook provides utilities to open a drawer, manage its type, and retrieve helper functions
 * for interacting with specific drawer instances.
 *
 * **Dependencies**:
 * - `useDrawer`: A custom hook that provides drawer-related functionalities.
 * - `useState`: React state management for storing the type of the drawer.
 * - `useCallback`: React hook for memoizing functions.
 *
 * @returns {UseDrawerList} An object containing:
 * - `drawer`: The drawer instance from `useDrawer`.
 * - `typeOfDrawer`: The current type of the drawer (`TypeOfDrawer`).
 * - `setTypeOfDrawer`: Function to update the type of the drawer.
 * - `openDrawer`: Function to open the drawer with a specific type.
 * - `helperOpenDrawer`: Function to retrieve helper information for opening a specific drawer instance.
 *
 * ### Functions:
 *
 * #### `openDrawer`
 * Opens the drawer with the specified type.
 *
 * @param {TypeOfDrawer} internalType - The type of the drawer to open.
 *
 * #### `helperOpenDrawer`
 * Retrieves helper information for opening a specific drawer instance.
 *
 * @param {Object} params - Parameters for the function.
 * @param {StyledTableDrawer['instances']} params.drawers - List of drawer instances.
 * @param {TypeOfDrawer} params.key - The type of the drawer to find.
 * @returns {HelperOpenDrawer | undefined} An object containing:
 * - `callback`: Function to open the drawer.
 * - `claimKey`: A unique key for the drawer instance.
 * - `customLabel`: A custom label for the drawer instance (default is an empty string).
 * Returns `undefined` if no matching drawer instance is found.
 *
 * ### Example Usage:
 * ```typescript
 * const {
 *   drawer,
 *   typeOfDrawer,
 *   setTypeOfDrawer,
 *   openDrawer,
 *   helperOpenDrawer,
 * } = useDrawerList();
 *
 * // Open a drawer with a specific type
 * openDrawer('exampleType');
 *
 * // Retrieve helper for a specific drawer instance
 * const helper = helperOpenDrawer({
 *   drawers: drawerInstances,
 *   key: 'exampleType',
 * });
 *
 * if (helper) {
 *   helper.callback(); // Opens the drawer
 *   console.log(helper.claimKey, helper.customLabel);
 * }
 * ```
 */
export const useDrawerList = (): UseDrawerList => {
  const drawer = useDrawer();
  const [typeOfDrawer, setTypeOfDrawer] = useState<TypeOfDrawer>();

  const openDrawer = useCallback(
    (internalType: TypeOfDrawer) => {
      setTypeOfDrawer(internalType);
      drawer.open();
    },
    [drawer],
  );

  const helperOpenDrawer = useCallback(
    ({
      drawers,
      key,
    }: {
      drawers: StyledTableDrawer['instances'];
      key: TypeOfDrawer;
    }): HelperOpenDrawer | undefined => {
      const drawerInstance = drawers.find(el => el.type === key);
      if (drawerInstance) {
        return {
          callback: () => openDrawer(key),
          claimKey: drawerInstance.claimKey,
          customLabel: drawerInstance.customLabel || '',
        };
      }
      return undefined;
    },
    [openDrawer],
  );

  return {
    drawer,
    typeOfDrawer,
    setTypeOfDrawer,
    openDrawer,
    helperOpenDrawer,
  };
};
